import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Receipt } from '../../types/expense';
import { useApiService } from '../../hooks/use-api-service';
import { ReceiptGrid } from './receipt-grid';
import { Button } from '../ui/button';
import { ScanLine, Upload, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { UploadReceiptDialog } from './upload-receipt-dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';

export function ReceiptsPage() {
  const { api, dataMode } = useApiService();
  const queryClient = useQueryClient();
  const [selectedReceipts, setSelectedReceipts] = useState<string[]>([]);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteBulkConfirmOpen, setDeleteBulkConfirmOpen] = useState(false);
  const [receiptToDelete, setReceiptToDelete] = useState<string | null>(null);
  const [processConfirmOpen, setProcessConfirmOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState({ current: 0, total: 0 });
  const [processingErrors, setProcessingErrors] = useState<Array<{ receiptId: string; error: string }>>([]);
  const pageRef = useRef<HTMLDivElement>(null);

  // Handle right-click context menu
  const handleContextMenu = (e: React.MouseEvent) => {
    // Only prevent default on the page container, not on cards
    const target = e.target as HTMLElement;
    if (target.closest('[data-receipt-card]')) {
      return; // Let receipt cards handle their own context menu
    }
    
    // Just prevent the default browser context menu - don't open anything
    e.preventDefault();
  };

  const { data: unprocessedReceipts = [], isLoading: loadingUnprocessed } = useQuery({
    queryKey: ['unprocessedReceipts', dataMode],
    queryFn: () => api.getUnprocessedReceipts(),
  });

  const { data: processedReceipts = [], isLoading: loadingProcessed } = useQuery({
    queryKey: ['processedReceipts', dataMode],
    queryFn: () => api.getProcessedReceipts(),
  });

  const processReceiptsMutation = useMutation({
    mutationFn: (receiptIds: string[]) => api.processReceipts(receiptIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unprocessedReceipts'] });
      queryClient.invalidateQueries({ queryKey: ['processedReceipts'] });
      queryClient.invalidateQueries({ queryKey: ['expenseClaims'] });
      setSelectedReceipts([]);
      toast.success('Receipts processed successfully!');
    },
    onError: () => {
      toast.error('Failed to process receipts');
    },
  });

  const deleteReceiptMutation = useMutation({
    mutationFn: async (receiptId: string) => {
      await api.deleteReceipt(receiptId);
      // Invalidate queries to sync with server
      await queryClient.invalidateQueries({ queryKey: ['unprocessedReceipts'] });
      // Add delay to ensure spinner shows while data refreshes
      await new Promise(resolve => setTimeout(resolve, 800));
    },
    onMutate: async (receiptId: string) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['unprocessedReceipts'] });
      
      // Snapshot the previous value
      const previousReceipts = queryClient.getQueryData(['unprocessedReceipts']);
      
      // Optimistically update to remove the receipt immediately
      queryClient.setQueryData(['unprocessedReceipts'], (old: any) => {
        if (!old) return old;
        return old.filter((receipt: any) => receipt.id !== receiptId);
      });
      
      // Return context with the snapshot
      return { previousReceipts };
    },
    onSuccess: () => {
      toast.success('Receipt deleted successfully!');
    },
    onError: (error, receiptId, context) => {
      // Rollback to the previous value on error
      if (context?.previousReceipts) {
        queryClient.setQueryData(['unprocessedReceipts'], context.previousReceipts);
      }
      toast.error('Failed to delete receipt');
    },
  });

  const deleteBulkReceiptsMutation = useMutation({
    mutationFn: async (receiptIds: string[]) => {
      await Promise.all(receiptIds.map(id => api.deleteReceipt(id)));
      // Invalidate queries to sync with server
      await queryClient.invalidateQueries({ queryKey: ['unprocessedReceipts'] });
      // Add delay to ensure spinner shows while data refreshes
      await new Promise(resolve => setTimeout(resolve, 800));
    },
    onMutate: async (receiptIds: string[]) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['unprocessedReceipts'] });
      
      // Snapshot the previous value
      const previousReceipts = queryClient.getQueryData(['unprocessedReceipts']);
      
      // Optimistically update to remove the receipts immediately
      queryClient.setQueryData(['unprocessedReceipts'], (old: any) => {
        if (!old) return old;
        return old.filter((receipt: any) => !receiptIds.includes(receipt.id));
      });
      
      // Return context with the snapshot
      return { previousReceipts };
    },
    onSuccess: () => {
      setSelectedReceipts([]);
      toast.success('Receipts deleted successfully!');
    },
    onError: (error, receiptIds, context) => {
      // Rollback to the previous value on error
      if (context?.previousReceipts) {
        queryClient.setQueryData(['unprocessedReceipts'], context.previousReceipts);
      }
      toast.error('Failed to delete receipts');
    },
  });

  const updateReceiptMutation = useMutation({
    mutationFn: ({ id, receipt }: { id: string; receipt: Partial<Receipt> }) => 
      api.updateReceipt(id, receipt),
    onSuccess: (data, { id, receipt }) => {
      queryClient.setQueryData(['unprocessedReceipts', dataMode], (prev: Receipt[] | undefined) => 
        prev?.map(r => r.id === id ? { ...r, ...receipt } : r)
      );
      queryClient.setQueryData(['processedReceipts', dataMode], (prev: Receipt[] | undefined) => 
        prev?.map(r => r.id === id ? { ...r, ...receipt } : r)
      );
      queryClient.invalidateQueries({ queryKey: ['unprocessedReceipts'] });
      queryClient.invalidateQueries({ queryKey: ['processedReceipts'] });
      toast.success('Receipt updated successfully!');
    },
    onError: () => {
      toast.error('Failed to update receipt');
    },
  });

  const handleProcessReceipts = () => {
    if (selectedReceipts.length === 0) {
      toast.error('Please select at least one receipt');
      return;
    }
    // Show confirmation dialog
    setProcessConfirmOpen(true);
  };

  const confirmProcessReceipts = async () => {
    setProcessConfirmOpen(false);
    setIsProcessing(true);
    setProcessingProgress({ current: 0, total: selectedReceipts.length });
    setProcessingErrors([]);

    const errors: Array<{ receiptId: string; error: string }> = [];
    let successCount = 0;

    // Process each receipt sequentially
    for (let i = 0; i < selectedReceipts.length; i++) {
      const receiptId = selectedReceipts[i];
      setProcessingProgress({ current: i + 1, total: selectedReceipts.length });

      try {
        await api.processExpense(receiptId);
        successCount++;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        errors.push({ receiptId, error: errorMessage });
      }
    }

    // Refresh data
    await queryClient.invalidateQueries({ queryKey: ['unprocessedReceipts'] });
    await queryClient.invalidateQueries({ queryKey: ['processedReceipts'] });
    await queryClient.invalidateQueries({ queryKey: ['expenseClaims'] });

    // Clear selection
    setSelectedReceipts([]);
    setIsProcessing(false);
    setProcessingProgress({ current: 0, total: 0 });

    // Show results
    if (errors.length === 0) {
      toast.success(`Successfully processed ${successCount} receipt${successCount > 1 ? 's' : ''}!`);
    } else if (successCount > 0) {
      toast.warning(`Processed ${successCount} receipt${successCount > 1 ? 's' : ''}, but ${errors.length} failed. Check console for details.`);
      console.error('Processing errors:', errors);
      setProcessingErrors(errors);
    } else {
      toast.error(`Failed to process all receipts. Check console for details.`);
      console.error('Processing errors:', errors);
      setProcessingErrors(errors);
    }
  };

  const handleReceiptSelect = (receiptId: string, selected: boolean) => {
    setSelectedReceipts((prev) =>
      selected ? [...prev, receiptId] : prev.filter((id) => id !== receiptId)
    );
  };

  const handleSelectAll = (receipts: Receipt[]) => {
    const allIds = receipts.map((r) => r.id);
    setSelectedReceipts(allIds);
  };

  const handleDeselectAll = () => {
    setSelectedReceipts([]);
  };

  const handleDeleteReceipt = (receiptId: string) => {
    setReceiptToDelete(receiptId);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteReceipt = () => {
    if (receiptToDelete) {
      deleteReceiptMutation.mutate(receiptToDelete);
      setDeleteConfirmOpen(false);
      setReceiptToDelete(null);
    }
  };

  const handleDeleteSelected = () => {
    if (selectedReceipts.length === 0) {
      toast.error('Please select at least one receipt');
      return;
    }
    setDeleteBulkConfirmOpen(true);
  };

  const confirmDeleteSelected = () => {
    deleteBulkReceiptsMutation.mutate(selectedReceipts);
    setDeleteBulkConfirmOpen(false);
  };

  const handleUpdateReceipt = (receipt: Receipt) => {
    updateReceiptMutation.mutate({ id: receipt.id, receipt });
  };

  return (
    <div 
      ref={pageRef}
      className="flex flex-col gap-6 p-4 md:p-6 relative" 
      data-tour="receipts-page"
      onContextMenu={handleContextMenu}
    >
      {/* Loading Overlay */}
      {(deleteReceiptMutation.isPending || deleteBulkReceiptsMutation.isPending) && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-card p-6 rounded-lg shadow-lg flex flex-col items-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm font-medium">Deleting receipt{deleteBulkReceiptsMutation.isPending ? 's' : ''}...</p>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Receipts</h1>
          <p className="text-muted-foreground mt-1 text-sm md:text-base">
            Manage your receipts and process expense claims
          </p>
        </div>
        <div data-tour="upload-button">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setUploadDialogOpen(true)} 
            className="w-full sm:w-auto"
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload Receipt
          </Button>
        </div>
      </div>

      <UploadReceiptDialog 
        open={uploadDialogOpen} 
        onOpenChange={setUploadDialogOpen} 
      />

      <div className="space-y-6">
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-lg md:text-xl font-semibold">Receipts</h2>
              <p className="text-sm text-muted-foreground">
                {selectedReceipts.length > 0
                  ? `${selectedReceipts.length} receipt(s) selected`
                  : 'Select receipts to process'}
              </p>
            </div>
            <div className="flex flex-wrap gap-2 w-full sm:w-auto">
              {selectedReceipts.length > 0 && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDeselectAll}
                  >
                    Deselect All
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleDeleteSelected}
                    disabled={deleteBulkReceiptsMutation.isPending}
                  >
                    {deleteBulkReceiptsMutation.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4 mr-2" />
                    )}
                    {deleteBulkReceiptsMutation.isPending
                      ? 'Deleting...'
                      : 'Delete Selected'}
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleProcessReceipts}
                    disabled={isProcessing}
                    data-tour="process-button"
                  >
                    {isProcessing ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <ScanLine className="h-4 w-4 mr-2" />
                    )}
                    {isProcessing
                      ? 'Processing...'
                      : 'Process Selected'}
                  </Button>
                </>
              )}
              {selectedReceipts.length === 0 &&
                unprocessedReceipts.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSelectAll(unprocessedReceipts)}
                  >
                    Select All
                  </Button>
                )}
            </div>
          </div>

          <ReceiptGrid
            receipts={unprocessedReceipts}
            selectedReceipts={selectedReceipts}
            onReceiptSelect={handleReceiptSelect}
            isLoading={loadingUnprocessed}
            data-tour="receipt-grid"
            onDelete={handleDeleteReceipt}
            showDelete={true}
          />
        </div>

        <div className="space-y-4">
          <div>
            <h2 className="text-xl font-semibold">Processed Receipts</h2>
            <p className="text-sm text-muted-foreground">
              Receipts that have been scanned and converted to expense claims
            </p>
          </div>

          <ReceiptGrid
            receipts={processedReceipts}
            selectedReceipts={[]}
            onReceiptSelect={() => {}}
            isLoading={loadingProcessed}
            selectable={false}
            onUpdate={handleUpdateReceipt}
          />
        </div>
      </div>

      {/* Delete Single Receipt Confirmation */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Receipt?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this receipt? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteReceipt} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Multiple Receipts Confirmation */}
      <AlertDialog open={deleteBulkConfirmOpen} onOpenChange={setDeleteBulkConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedReceipts.length} Receipt{selectedReceipts.length > 1 ? 's' : ''}?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedReceipts.length} selected receipt{selectedReceipts.length > 1 ? 's' : ''}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteSelected} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Process Receipts Confirmation */}
      <AlertDialog open={processConfirmOpen} onOpenChange={setProcessConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Process {selectedReceipts.length} Receipt{selectedReceipts.length > 1 ? 's' : ''}?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to process {selectedReceipts.length} selected receipt{selectedReceipts.length > 1 ? 's' : ''}? This will create expense claims for each receipt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmProcessReceipts}>
              Yes, Process
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Processing Overlay */}
      {isProcessing && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-card p-8 rounded-lg shadow-lg flex flex-col items-center gap-4 min-w-[300px]">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <div className="text-center space-y-2">
              <p className="text-lg font-semibold">Processing Receipts</p>
              <p className="text-sm text-muted-foreground">
                Processing {processingProgress.current} of {processingProgress.total}
              </p>
              <div className="w-full bg-secondary rounded-full h-2 mt-4">
                <div 
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(processingProgress.current / processingProgress.total) * 100}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
